/**
 * TransactionRow Component
 * Displays a bank transaction with match status badge and action buttons
 */

import { useState } from 'react';
import type { BankTransaction } from '../api/external-payments.api';
import {
  confirmTransactionMatch,
  dismissTransaction,
  manualMatchTransaction,
} from '../api/external-payments.api';
import { MatchStatusBadge } from './MatchStatusBadge';
import { formatLocalDate } from '../utils/date';
import { formatUnitLabel } from '@resida/shared';

export interface TenantOption {
  tenantId: string;
  leaseId: string;
  firstName: string;
  lastName: string;
  unitNumber: string | null;
  address1: string;
}

interface TransactionRowProps {
  transaction: BankTransaction;
  tenantOptions: TenantOption[];
  onUpdated: () => void;
}

export function TransactionRow({ transaction, tenantOptions, onUpdated }: TransactionRowProps): JSX.Element {
  const [isConfirming, setIsConfirming] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const [showMatchForm, setShowMatchForm] = useState(false);
  const [selectedTenantKey, setSelectedTenantKey] = useState('');
  const [isMatching, setIsMatching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { id, amount, date, description, matchStatus, transactionMatch } = transaction;

  const handleConfirm = async () => {
    try {
      setIsConfirming(true);
      setError(null);
      await confirmTransactionMatch(id);
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to confirm');
    } finally {
      setIsConfirming(false);
    }
  };

  const handleDismiss = async () => {
    try {
      setIsDismissing(true);
      setError(null);
      await dismissTransaction(id);
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to dismiss');
    } finally {
      setIsDismissing(false);
    }
  };

  const handleManualMatch = async (e: React.FormEvent) => {
    e.preventDefault();
    const selected = tenantOptions.find(
      (t) => `${t.tenantId}:${t.leaseId}` === selectedTenantKey
    );
    if (!selected) return;
    try {
      setIsMatching(true);
      setError(null);
      await manualMatchTransaction(id, { leaseId: selected.leaseId, tenantId: selected.tenantId });
      setShowMatchForm(false);
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to match');
    } finally {
      setIsMatching(false);
    }
  };

  const formattedDate = formatLocalDate(date);
  const formattedAmount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);

  return (
    <div
      data-testid={`transaction-row-${id}`}
      className="bg-card rounded-lg p-4 border border-border mb-2"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{description}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{formattedDate}</p>
          {transactionMatch && (
            <p className="text-xs text-muted-foreground mt-1">
              Tenant ID: {transactionMatch.tenantId} · {transactionMatch.confidence} confidence
            </p>
          )}
          {transactionMatch?.matchReasoning && (
            <p
              data-testid={`match-reasoning-${id}`}
              className="text-xs text-muted-foreground mt-1 italic"
            >
              {transactionMatch.matchReasoning}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className="text-sm font-semibold text-foreground">{formattedAmount}</span>
          <MatchStatusBadge status={matchStatus} id={id} />
        </div>
      </div>

      {error && <p className="text-xs text-destructive mt-2">{error}</p>}

      <div className="mt-3 flex gap-2 flex-wrap">
        {matchStatus === 'suggested' && (
          <>
            <button
              type="button"
              data-testid={`confirm-match-button-${id}`}
              onClick={handleConfirm}
              disabled={isConfirming}
              className="px-3 py-1.5 text-xs font-medium text-success-foreground bg-success rounded hover:bg-success/90 disabled:opacity-50 min-h-[36px]"
            >
              {isConfirming ? 'Confirming...' : 'Confirm'}
            </button>
            <button
              type="button"
              data-testid={`dismiss-transaction-button-${id}`}
              onClick={handleDismiss}
              disabled={isDismissing}
              className="px-3 py-1.5 text-xs font-medium text-foreground border border-border rounded hover:bg-accent disabled:opacity-50 min-h-[36px]"
            >
              {isDismissing ? 'Dismissing...' : 'Dismiss'}
            </button>
          </>
        )}

        {matchStatus === 'unmatched' && (
          <>
            <button
              type="button"
              data-testid={`manual-match-button-${id}`}
              onClick={() => setShowMatchForm(!showMatchForm)}
              className="px-3 py-1.5 text-xs font-medium text-primary border border-primary/20 rounded hover:bg-primary/5 min-h-[36px]"
            >
              Match to Tenant
            </button>
            <button
              type="button"
              data-testid={`dismiss-transaction-button-${id}`}
              onClick={handleDismiss}
              disabled={isDismissing}
              className="px-3 py-1.5 text-xs font-medium text-muted-foreground border border-border rounded hover:bg-accent disabled:opacity-50 min-h-[36px]"
            >
              {isDismissing ? 'Dismissing...' : 'Dismiss'}
            </button>
          </>
        )}
      </div>

      {showMatchForm && (
        <form
          onSubmit={handleManualMatch}
          className="mt-3 p-3 bg-accent/50 rounded border border-border"
        >
          <h4 className="text-xs font-semibold text-foreground mb-2">Match to Tenant</h4>
          <div>
            <label htmlFor={`tenant-select-${id}`} className="block text-xs text-muted-foreground mb-1">
              Tenant
            </label>
            <select
              id={`tenant-select-${id}`}
              data-testid={`tenant-select-${id}`}
              value={selectedTenantKey}
              onChange={(e) => setSelectedTenantKey(e.target.value)}
              className="w-full px-2 py-1.5 text-xs border border-input rounded bg-card focus:outline-none focus:ring-1 focus:ring-ring"
              required
              aria-required="true"
            >
              <option value="">Select a tenant...</option>
              {tenantOptions.map((t) => (
                <option key={`${t.tenantId}:${t.leaseId}`} value={`${t.tenantId}:${t.leaseId}`}>
                  {t.firstName} {t.lastName}{formatUnitLabel(t.unitNumber) ? ` — ${formatUnitLabel(t.unitNumber)}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="mt-2 flex gap-2">
            <button
              type="submit"
              disabled={isMatching}
              className="px-3 py-1.5 text-xs font-medium text-primary-foreground bg-primary rounded hover:bg-primary/90 disabled:opacity-50 min-h-[36px]"
            >
              {isMatching ? 'Matching...' : 'Save Match'}
            </button>
            <button
              type="button"
              onClick={() => setShowMatchForm(false)}
              className="px-3 py-1.5 text-xs font-medium text-foreground border border-border rounded hover:bg-accent min-h-[36px]"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
